import React from 'react';
import type { FC } from 'react';
import { styled } from '@compiled/react';

import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { expValEquals } from '@confluence/feature-experiments';
import { TwoStageHydration } from '@confluence/ssr-utilities/entry-points/TwoStageHydration.ts';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';

import { LivePagesByLine } from './LivePagesByLine.tsx';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EditorByLineWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});

export type EditorByLineAreaProps = {
	contentId: string;
	contentType: string;
	isPublishingContent: boolean;
	userOffline: boolean;
	isKnowledgeBaseSpace: boolean;
	viewMode?: boolean;
	editorApi?: FullPageEditorPresetAPI | null;
	isUnpublishedDraft?: boolean;
	isSpaceOverview?: boolean;
	hasByLineContributors: boolean;
	hasByLineExtensions: boolean;
	hasReactions: boolean;
};

export const EditorByLineArea: FC<EditorByLineAreaProps> = (props) =>
	props.hasByLineExtensions &&
	!props.isSpaceOverview &&
	expValEquals('confluence_ssr_byline', 'isEnabled', true) ? (
		<SuspenseWithDataFetcher dataFetcherName="byline-apps">
			<EditorByLineAreaInner {...props} />
		</SuspenseWithDataFetcher>
	) : (
		<EditorByLineAreaInner {...props} />
	);

const EditorByLineAreaInner: FC<EditorByLineAreaProps> = ({
	contentId,
	contentType,
	isPublishingContent,
	userOffline,
	isKnowledgeBaseSpace,
	viewMode,
	editorApi,
	isUnpublishedDraft,
	isSpaceOverview,
	hasByLineContributors,
	hasByLineExtensions,
	hasReactions,
}) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();

	if (isSpaceOverview) {
		return null;
	}

	return (
		<TwoStageHydration
			name="editor-byline"
			// cc_editor_two_stage_hydration_removal - When cleaning up this experiment, also remove the related TwoStageHydration component
			enabled={!expValEquals('cc_editor_two_stage_hydration_removal', 'editorByLineArea', true)}
		>
			<EditorByLineWrapper data-vc="editor-byline-wrapper" {...ssrPlaceholderIdProp}>
				<LivePagesByLine
					contentId={contentId}
					contentType={contentType}
					isPublishingContent={isPublishingContent}
					userOffline={userOffline}
					isKnowledgeBaseSpace={isKnowledgeBaseSpace}
					viewMode={viewMode}
					editorApi={editorApi}
					isUnpublishedDraft={isUnpublishedDraft}
					hasByLineContributors={hasByLineContributors}
					hasByLineExtensions={hasByLineExtensions}
					hasReactions={hasReactions}
				/>
			</EditorByLineWrapper>
		</TwoStageHydration>
	);
};
